/** @format */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CoreRef, PaperQualification } from "@question-bank/shared";
import { listQualifications, listSeries, listSubjects, listTiers } from "../api/coreTaxonomy";
import { useBrand } from "../hooks/useBrand";
import { questionImportService } from "../services/questionImportService";
import { Locale } from "../features/i18n/init";
import { notify } from "./Toast/toast";
import { PaperLevelForm, type PaperLevelOption } from "./PaperLevelForm";
import { EMPTY_PAPER_LEVEL, isPaperLevelComplete, type PaperLevelValues } from "./paperLevel";
import { mergeBlankPaperLevel, paperLevelFromCover } from "./paperLevelFromCover";
import { readPaperCover } from "./readPaperCover";
import type { ImportPdfKeys } from "./PdfImport";

interface PdfImportStartProps {
	keys: ImportPdfKeys;
	paperFile?: File | null;
}

interface CoreItem {
	id: string;
	title: { en: string };
}

/**
 * Collects the paper-level facts for an uploaded set of PDFs, then creates the
 * paper and the PDF import job and starts its extraction pipeline.
 */
export const PdfImportStart = ({ keys, paperFile = null }: PdfImportStartProps) => {
	const { brand } = useBrand();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { locale } = useParams<{ locale: Locale }>();
	const [values, setValues] = useState<PaperLevelValues>(EMPTY_PAPER_LEVEL);
	const [subjects, setSubjects] = useState<string[]>([]);
	const [qualifications, setQualifications] = useState<PaperLevelOption[]>([]);
	const [tiers, setTiers] = useState<CoreItem[]>([]);
	const [series, setSeries] = useState<CoreItem[]>([]);
	const [hasLoadedLists, setHasLoadedLists] = useState(false);
	const [hasCoverSettled, setHasCoverSettled] = useState(paperFile === null);
	const [hasCoverReadFailed, setHasCoverReadFailed] = useState(false);
	const [isStarting, setIsStarting] = useState(false);

	// Core is a hard dependency: a failed read leaves its dropdown empty.
	useEffect(() => {
		let isCancelled = false;
		setHasLoadedLists(false);
		const load = <T,>(request: Promise<{ data: T[] }>, apply: (items: T[]) => void) =>
			request.then((list) => !isCancelled && apply(list.data)).catch(() => !isCancelled && apply([]));
		void Promise.all([
			load(listSubjects(brand), (items) => setSubjects(items.map((item) => item.title.en))),
			load(listQualifications(brand), (items) =>
				setQualifications(items.map((item) => ({ value: item.code, label: item.title.en }))),
			),
			load(listTiers(brand), setTiers),
			load(listSeries(brand), setSeries),
		]).finally(() => {
			if (!isCancelled) {
				setHasLoadedLists(true);
			}
		});
		return () => {
			isCancelled = true;
		};
	}, [brand]);

	const tierOptions = useMemo(() => tiers.map((item) => ({ value: item.id, label: item.title.en })), [tiers]);
	const seriesOptions = useMemo(() => series.map((item) => ({ value: item.id, label: item.title.en })), [series]);
	const catalogRef = useRef({ subjects, qualifications, tiers: tierOptions, series: seriesOptions });
	catalogRef.current = { subjects, qualifications, tiers: tierOptions, series: seriesOptions };

	// Lists stay in a ref so a later list render does not cancel the cover read.
	useEffect(() => {
		if (!paperFile) {
			setHasCoverSettled(true);
			return;
		}
		if (!hasLoadedLists) {
			setHasCoverSettled(false);
			return;
		}
		let isCancelled = false;
		setHasCoverReadFailed(false);
		setHasCoverSettled(false);
		void readPaperCover(paperFile).then((cover) => {
			if (isCancelled) {
				return;
			}
			setHasCoverSettled(true);
			if (!cover) {
				setHasCoverReadFailed(true);
				return;
			}
			setValues((current) => mergeBlankPaperLevel(current, paperLevelFromCover(cover, catalogRef.current)));
		});
		return () => {
			isCancelled = true;
		};
	}, [paperFile, hasLoadedLists]);

	const isWaitingForCover = paperFile !== null && !hasCoverSettled;

	const handleStart = async () => {
		const qualification: PaperQualification = {
			code: values.qualification,
			...(qualifications.find((o) => o.value === values.qualification)?.label
				? { title: qualifications.find((o) => o.value === values.qualification)?.label }
				: {}),
		};
		const seasonTitle = seriesOptions.find((o) => o.value === values.season)?.label;
		const season: CoreRef = { code: values.season, ...(seasonTitle ? { title: seasonTitle } : {}) };
		const tierTitle = tierOptions.find((o) => o.value === values.tier)?.label;
		const tier: CoreRef | undefined = values.tier
			? { code: values.tier, ...(tierTitle ? { title: tierTitle } : {}) }
			: undefined;

		setIsStarting(true);
		try {
			const job = await questionImportService.importPdfs(
				brand,
				{
					paperCode: values.paperCode.trim(),
					subject: values.subject.trim(),
					qualification,
					...(tier ? { tier } : {}),
					season,
					year: Number.parseInt(values.year, 10),
					date: values.date,
				},
				keys,
			);
			notify("success", "PDF import started");
			navigate(`/${locale}/admin/import-jobs/${job.id}`);
		} catch (err) {
			notify("error", err instanceof Error ? err.message : "Failed to start PDF import");
		} finally {
			setIsStarting(false);
		}
	};

	return (
		<div className="mt-6">
			{isWaitingForCover && (
				<p className="mb-3 text-sm text-slate-600" role="status">
					{t("pdfImport.readingPaperDetails")}
				</p>
			)}
			{hasCoverReadFailed && (
				<p className="mb-3 text-sm text-slate-600" role="status">
					{t("pdfImport.paperDetailsUnread")}
				</p>
			)}
			<PaperLevelForm
				values={values}
				onChange={setValues}
				subjectOptions={subjects}
				qualificationOptions={qualifications}
				tierOptions={tierOptions}
				seriesOptions={seriesOptions}
				isDisabled={isStarting}
				isBusy={isWaitingForCover}
			/>
			<button
				type="button"
				onClick={() => void handleStart()}
				disabled={!isPaperLevelComplete(values) || isStarting || isWaitingForCover}
				className="mt-6 rounded-md bg-(--admin-color-primary-strong) px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
			>
				{isStarting ? "Starting…" : "Start import"}
			</button>
		</div>
	);
};
